/* global google */
import React, { useEffect, useRef, useState } from "react";
import { Map, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";

const MapContainer = ({
  showDeleteButton,
  onDeleteShape,
  onPolygonChange,
  shapesDeleted, // 👈 receive this from parent
}) => {
  const [center] = useState({ lat: 11.1271, lng: 78.6569 });
  const [polygons, setPolygons] = useState([]);

  const mapRef = useRef(null);
  const drawingManagerRef = useRef(null);
  const polygonRefs = useRef([]); // store actual google polygon instances

  const map = useMap();
  const drawing = useMapsLibrary("drawing");
  const places = useMapsLibrary("places");

  // Initialize DrawingManager once both map and drawing lib are ready
  useEffect(() => {
    if (!map || !drawing) return;

    const polygonOptions = {
      fillOpacity: 0.3,
      fillColor: "#ff0000",
      strokeColor: "#ff0000",
      strokeWeight: 2,
      draggable: true,
      editable: true,
    };

    const manager = new drawing.DrawingManager({
      drawingMode: drawing.OverlayType.POLYGON,
      drawingControl: true,
      drawingControlOptions: {
        position: google.maps.ControlPosition.BOTTOM_RIGHT,
        drawingModes: [drawing.OverlayType.POLYGON],
      },
      polygonOptions,
    });

    manager.setMap(map);
    drawingManagerRef.current = manager;

    google.maps.event.addListener(manager, "overlaycomplete", (event) => {
      if (event.type === "polygon") {
        const overlay = event.overlay;
        overlay.setEditable(true);
        overlay.setDraggable(true);

        // Store polygon instance
        polygonRefs.current.push(overlay);

        // Get coordinates
        const path = overlay
          .getPath()
          .getArray()
          .map((latLng) => ({ lat: latLng.lat(), lng: latLng.lng() }));
        const closedPath = [...path, path[0]];

        const updatedPolygons = [...polygons, closedPath];
        setPolygons(updatedPolygons);
        onPolygonChange(updatedPolygons.flat());

        // Listen for edits
        google.maps.event.addListener(overlay.getPath(), "set_at", () => {
          const newCoords = overlay
            .getPath()
            .getArray()
            .map((latLng) => ({ lat: latLng.lat(), lng: latLng.lng() }));
          onPolygonChange(newCoords);
        });
        google.maps.event.addListener(overlay.getPath(), "insert_at", () => {
          const newCoords = overlay
            .getPath()
            .getArray()
            .map((latLng) => ({ lat: latLng.lat(), lng: latLng.lng() }));
          onPolygonChange(newCoords);
        });
      }
    });

    return () => manager.setMap(null);
  }, [map, drawing]);

  // Initialize Autocomplete for search
  useEffect(() => {
    if (!map || !places) return;

    const input = document.getElementById("searchBox");
    if (!input) return;

    const autocomplete = new places.Autocomplete(input);
    autocomplete.bindTo("bounds", map);

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (!place.geometry) return;
      if (place.geometry.viewport) {
        map.fitBounds(place.geometry.viewport);
      } else {
        map.setCenter(place.geometry.location);
        map.setZoom(15);
      }
    });
  }, [map, places]);

  // 🧹 When shapesDeleted changes, clear all polygons
  useEffect(() => {
    if (shapesDeleted > 0) {
      polygonRefs.current.forEach((polygon) => polygon.setMap(null)); // remove each polygon from map
      polygonRefs.current = [];
      setPolygons([]);
      onPolygonChange([]);
    }
  }, [shapesDeleted]); // ✅ triggers on every delete

  return (
    <div style={{ position: "relative" }}>
      {/* Search box */}
      <input
        id="searchBox"
        type="text"
        placeholder="Search Location"
        style={{
          boxSizing: "border-box",
          border: "1px solid #ccc",
          width: "250px",
          height: "40px",
          padding: "0 12px",
          borderRadius: "6px",
          fontSize: "14px",
          outline: "none",
          position: "absolute",
          right: "10px",
          top: "10px",
          zIndex: 10,
        }}
      />

      <Map
        defaultZoom={14}
        defaultCenter={center}
        style={{ width: "100%", height: "450px", borderRadius: "10px" }}
        onLoad={(mapInstance) => (mapRef.current = mapInstance)}
      />
    </div>
  );
};

export default MapContainer;
